import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { getCanonicalVehicleStatus } from "../../utils/vehicleStatus";
import {
  useGetAllLiteDevicesQuery,
  useGetDevicesPageQuery,
} from "../../redux/admin/adminApi";

const safeLower = (value) => String(value || "").toLowerCase();
const ASSIGN_DEVICE_PAGE_SIZE = 10;

export default function GeofenceAssignModal({
  open,
  geofence,
  devices = [],
  onClose,
  onToggleDevice,
  pendingDeviceIds = {},
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setPage(1);
    }
  }, [open]);

  const pagedQueryArgs = useMemo(
    () => ({
      page,
      limit: ASSIGN_DEVICE_PAGE_SIZE,
      lite: "true",
      includeExpired: "true",
      search: search.trim() || undefined,
    }),
    [page, search]
  );

  const { data: pagedDevicesResponse, isFetching: isPagedDevicesFetching } = useGetDevicesPageQuery(
    pagedQueryArgs,
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: fallbackDevices = [], isFetching: isFallbackDevicesFetching } = useGetAllLiteDevicesQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });

  const sourceDevices = useMemo(() => {
    if (Array.isArray(fallbackDevices)) return fallbackDevices;
    if (Array.isArray(devices) && devices.length) return devices;
    if (Array.isArray(devices?.data) && devices.data.length) return devices.data;
    return [];
  }, [devices, fallbackDevices]);

  const assignedIds = useMemo(
    () =>
      new Set(
        (Array.isArray(geofence?.assignedDeviceIds) ? geofence.assignedDeviceIds : []).map((value) =>
          String(value)
        )
      ),
    [geofence?.assignedDeviceIds]
  );

  const fallbackRows = useMemo(() => {
    const q = safeLower(search).trim();

    return sourceDevices
      .filter((device) => {
        if (!q) return true;
        return (
          safeLower(device?.name).includes(q) ||
          safeLower(device?.imei).includes(q) ||
          safeLower(device?.msdn).includes(q) ||
          safeLower(device?.model).includes(q)
        );
      })
      .map((device) => ({
        ...device,
        checked:
          pendingDeviceIds?.[String(device?._id || "")] ?? assignedIds.has(String(device?._id || "")),
        status: getCanonicalVehicleStatus(device),
      }));
  }, [assignedIds, pendingDeviceIds, search, sourceDevices]);

  const pagedRows = useMemo(() => {
    const source = Array.isArray(pagedDevicesResponse?.data) ? pagedDevicesResponse.data : [];
    return source.map((device) => ({
      ...device,
      checked:
        pendingDeviceIds?.[String(device?._id || "")] ?? assignedIds.has(String(device?._id || "")),
      status: getCanonicalVehicleStatus(device),
    }));
  }, [assignedIds, pagedDevicesResponse?.data, pendingDeviceIds]);

  const searchActive = Boolean(search.trim());
  const filteredSearchRows = useMemo(() => fallbackRows, [fallbackRows]);
  const searchPageRows = useMemo(() => {
    const start = Math.max(0, (page - 1) * ASSIGN_DEVICE_PAGE_SIZE);
    return filteredSearchRows.slice(start, start + ASSIGN_DEVICE_PAGE_SIZE);
  }, [filteredSearchRows, page]);
  const rows = searchActive ? searchPageRows : pagedRows;

  const totalPages = useMemo(() => {
    if (searchActive) {
      return Math.max(1, Math.ceil(filteredSearchRows.length / ASSIGN_DEVICE_PAGE_SIZE));
    }
    const total = Number(pagedDevicesResponse?.total || 0);
    return Math.max(1, Math.ceil(total / ASSIGN_DEVICE_PAGE_SIZE));
  }, [filteredSearchRows.length, pagedDevicesResponse?.total, searchActive]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth>
      <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Assign Devices
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2.5 }}>
        <TextField
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "54px 1.1fr 1.2fr 1fr 1.2fr 1.1fr 1fr",
            gap: 1,
            px: 1,
            py: 1,
            borderBottom: "1px solid #e5e7eb",
            color: "text.secondary",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          <Box />
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Vehicle No</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>IMEI</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Status</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Last Update</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Sim No</Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Model</Typography>
        </Box>
        <Box sx={{ maxHeight: 560, overflowY: "auto" }}>
          {(searchActive ? isFallbackDevicesFetching : isPagedDevicesFetching) && rows.length === 0 ? (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : rows.map((device) => {
            const deviceId = String(device?._id || "");
            const pending = pendingDeviceIds?.[deviceId] === true;
            return (
              <Box
                key={deviceId}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "54px 1.1fr 1.2fr 1fr 1.2fr 1.1fr 1fr",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  borderBottom: "1px solid #f1f5f9",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={device.checked}
                  disabled={pending}
                  onChange={(event) => onToggleDevice?.(device, event.target.checked)}
                />
                <Typography sx={{ fontSize: "12px" }}>{device?.vehicleNumber || device?.name || "-"}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.imei || "-"}</Typography>
                <Typography sx={{ fontSize: "12px", textTransform: "capitalize" }}>{String(device.status || "-").replaceAll("_", " ")}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.lastDataReceivedAt ? new Date(device.lastDataReceivedAt).toLocaleString() : "-"}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.msdn || "-"}</Typography>
                <Typography sx={{ fontSize: "12px" }}>{device?.model || "-"}</Typography>
              </Box>
            );
          })}
          {!isPagedDevicesFetching && !rows.length ? (
            <Box sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
              No devices found.
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            Showing page {Math.min(page, totalPages)} of {totalPages}
          </Typography>
          <Pagination
            page={Math.min(page, totalPages)}
            count={totalPages}
            onChange={(_, nextPage) => setPage(nextPage)}
            color="primary"
            size="small"
            shape="rounded"
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
